/**
 * Maater UI gallery scene: member identity + who's here.
 * Placement E is locked. Chip face and tooltip stay a high-level abstract.
 * Hash: /?test=maater-ui#presence
 * Bet: docs/product/bets/2026-08-28-member-identity-and-whos-here-presence.md
 *
 * The chip and tooltip in this file are a sketch of that abstract
 * (who, and what they are doing on this document). They are not the shared
 * presence-chip component. Cluster and tab pip must become that one component.
 */
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Check, MoreHorizontal, Pencil } from 'lucide-react'
import { cn } from '@/lib/utils'
import { testIds } from '@/lib/test-ids'
import { SHELL_COLUMN_HEADER_CLASS } from '@/lib/shell-chrome'

type PlacementId = 'a' | 'b' | 'c' | 'd' | 'e'
type FocusMode = 'about-focused' | 'notes-focused'

type DemoMember = {
  id: string
  screenName: string
  fullName: string
  email: string
  initials: string
  photoUrl?: string
  presence: 'focused' | 'background'
  action: 'Editing' | 'Viewing'
  lastEdit: string
}

const MEMBERS: DemoMember[] = [
  {
    id: 'chris',
    screenName: 'Chris',
    fullName: 'Chris Townsend',
    email: 'chris@hopstudios.com',
    initials: 'CT',
    photoUrl: 'https://i.pravatar.cc/64?u=chris-townsend',
    presence: 'focused',
    action: 'Editing',
    lastEdit: '2 hr ago',
  },
  {
    id: 'tammy',
    screenName: 'Tammy',
    fullName: 'Tammy Hart',
    email: 'tammy@hopstudios.com',
    initials: 'TH',
    photoUrl: 'https://i.pravatar.cc/64?u=tammy-hart',
    presence: 'background',
    action: 'Viewing',
    lastEdit: '8 hr ago',
  },
  {
    id: 'gilbert',
    screenName: 'Gilbert',
    fullName: 'Gilbert Lin',
    email: 'gilbert@hopstudios.com',
    initials: 'GL',
    presence: 'background',
    action: 'Viewing',
    lastEdit: '1 day ago',
  },
]

const PLACEMENTS: {
  id: PlacementId
  label: string
  title: string
  note: string
  rank: 'locked' | 'ok' | 'out'
}[] = [
  {
    id: 'a',
    label: 'A',
    title: 'Title-row cluster',
    note: 'Stacked bubbles on the address row, before ... . Quiet. Blind to other tabs.',
    rank: 'ok',
  },
  {
    id: 'b',
    label: 'B',
    title: 'Tab-face pip',
    note: 'Tiny avatar on the document tab. Visible while you work elsewhere. Crowds labels.',
    rank: 'ok',
  },
  {
    id: 'c',
    label: 'C',
    title: 'Status-bar whisper',
    note: 'Chris, Tammy here on the document status bar. Easy to miss.',
    rank: 'out',
  },
  {
    id: 'd',
    label: 'D',
    title: 'Info dock strip',
    note: 'Who\'s here on Details. Hidden when the dock is collapsed.',
    rank: 'out',
  },
  {
    id: 'e',
    label: 'E',
    title: 'Hybrid A + B',
    note: 'Locked. Focused doc gets the title-row cluster. Background tabs get a ghosted pip.',
    rank: 'locked',
  },
]

function DemoAvatar({
  member,
  size = 28,
  presence,
}: {
  member: DemoMember
  size?: number
  presence?: DemoMember['presence']
}) {
  const state = presence ?? member.presence
  const focused = state === 'focused'
  return (
    <span
      className="relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full text-[10px] font-medium"
      style={{
        width: size,
        height: size,
        boxShadow: focused
          ? '0 0 0 2px hsl(var(--background)), 0 0 0 3.5px hsl(var(--primary))'
          : '0 0 0 2px hsl(var(--background))',
        opacity: focused ? 1 : 0.55,
        background: member.photoUrl ? 'transparent' : 'hsl(var(--muted))',
        color: 'hsl(var(--foreground))',
      }}
      aria-hidden
    >
      {member.photoUrl ? (
        <img src={member.photoUrl} alt="" className="h-full w-full object-cover" />
      ) : (
        member.initials
      )}
    </span>
  )
}

const CARD_W = 256
const CARD_H = 112

function placeNearPointer(clientX: number, clientY: number) {
  const gap = 14
  const pad = 8
  let x = clientX + gap
  let y = clientY + gap
  if (x + CARD_W > window.innerWidth - pad) x = clientX - CARD_W - gap
  if (y + CARD_H > window.innerHeight - pad) y = clientY - CARD_H - gap
  x = Math.max(pad, Math.min(x, window.innerWidth - CARD_W - pad))
  y = Math.max(pad, Math.min(y, window.innerHeight - CARD_H - pad))
  return { x, y }
}

function placeNearTrigger(el: HTMLElement) {
  const rect = el.getBoundingClientRect()
  const gap = 8
  const pad = 8
  let x = rect.left + rect.width / 2 - CARD_W / 2
  let y = rect.bottom + gap
  if (y + CARD_H > window.innerHeight - pad) y = rect.top - CARD_H - gap
  x = Math.max(pad, Math.min(x, window.innerWidth - CARD_W - pad))
  y = Math.max(pad, Math.min(y, window.innerHeight - CARD_H - pad))
  return { x, y }
}

function PersonCardBody({ member }: { member: DemoMember }) {
  return (
    <div className="flex gap-3 p-3">
      <DemoAvatar member={member} size={40} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{member.screenName}</p>
        <p className="truncate text-xs text-muted-foreground">{member.fullName}</p>
        <p className="mt-1.5 text-xs">
          {member.action}
          {' · '}
          {member.presence === 'focused' ? 'focused tab' : 'tab in background'}
        </p>
        <p className="text-xs text-muted-foreground">Last edit {member.lastEdit}</p>
      </div>
    </div>
  )
}

function HoverCard({
  member,
  children,
  block = false,
  linger = false,
  className,
  style,
}: {
  member: DemoMember
  children: ReactNode
  block?: boolean
  /** Larger rest on show/hide. Card stays put instead of chasing the pointer. */
  linger?: boolean
  className?: string
  style?: CSSProperties
}) {
  const rootRef = useRef<HTMLSpanElement>(null)
  const openTimer = useRef<number | null>(null)
  const closeTimer = useRef<number | null>(null)
  const unmountTimer = useRef<number | null>(null)
  const fadeInRef = useRef<number | null>(null)
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null)
  const [visible, setVisible] = useState(false)
  const fadeMs = linger ? 240 : 0

  useEffect(() => {
    return () => {
      if (openTimer.current) window.clearTimeout(openTimer.current)
      if (closeTimer.current) window.clearTimeout(closeTimer.current)
      if (unmountTimer.current) window.clearTimeout(unmountTimer.current)
      if (fadeInRef.current) window.clearTimeout(fadeInRef.current)
    }
  }, [])

  function clearOpen() {
    if (openTimer.current) window.clearTimeout(openTimer.current)
    openTimer.current = null
  }

  function clearClose() {
    if (closeTimer.current) window.clearTimeout(closeTimer.current)
    if (unmountTimer.current) window.clearTimeout(unmountTimer.current)
    closeTimer.current = null
    unmountTimer.current = null
  }

  function cancelFadeIn() {
    if (fadeInRef.current) window.clearTimeout(fadeInRef.current)
    fadeInRef.current = null
  }

  function reveal(clientX: number, clientY: number) {
    const next = linger && rootRef.current
      ? placeNearTrigger(rootRef.current)
      : placeNearPointer(clientX, clientY)
    setPos(next)
    if (!linger) {
      setVisible(true)
      return
    }
    setVisible(false)
    cancelFadeIn()
    fadeInRef.current = window.setTimeout(() => {
      fadeInRef.current = null
      setVisible(true)
    }, 32)
  }

  function scheduleHide() {
    cancelFadeIn()
    closeTimer.current = window.setTimeout(() => {
      setVisible(false)
      unmountTimer.current = window.setTimeout(() => setPos(null), fadeMs)
    }, linger ? 160 : 0)
  }

  return (
    <span
      ref={rootRef}
      className={cn(block ? 'relative block w-full' : 'relative inline-flex', className)}
      style={style}
      onMouseEnter={(e) => {
        clearClose()
        if (pos) {
          setVisible(true)
          return
        }
        const x = e.clientX
        const y = e.clientY
        clearOpen()
        openTimer.current = window.setTimeout(() => reveal(x, y), linger ? 180 : 0)
      }}
      onMouseMove={(e) => {
        if (linger || !pos) return
        setPos(placeNearPointer(e.clientX, e.clientY))
      }}
      onMouseLeave={() => {
        clearOpen()
        scheduleHide()
      }}
    >
      {children}
      {pos
        ? createPortal(
            <div
              role="tooltip"
              className="fixed z-[80] w-64 overflow-hidden rounded-md border border-border text-popover-foreground shadow-lg"
              style={{
                left: pos.x,
                top: pos.y,
                backgroundColor: 'hsl(var(--popover))',
                color: 'hsl(var(--popover-foreground))',
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0) scale(1)' : 'translateY(4px) scale(0.98)',
                transition: linger
                  ? `opacity ${fadeMs}ms cubic-bezier(0.22, 1, 0.36, 1), transform ${fadeMs}ms cubic-bezier(0.22, 1, 0.36, 1)`
                  : undefined,
                pointerEvents: linger && visible ? 'auto' : 'none',
              }}
              onMouseEnter={() => {
                if (!linger) return
                clearClose()
                setVisible(true)
              }}
              onMouseLeave={() => {
                if (!linger) return
                scheduleHide()
              }}
            >
              <PersonCardBody member={member} />
            </div>,
            document.body,
          )
        : null}
    </span>
  )
}

function AvatarCluster({
  members,
  size = 28,
}: {
  members: DemoMember[]
  size?: number
}) {
  const [spread, setSpread] = useState(false)
  const [frontId, setFrontId] = useState<string | null>(null)
  const overlap = -10
  const gap = 12
  const ease = '420ms cubic-bezier(0.22, 1, 0.36, 1)'

  return (
    <div
      className="relative flex items-center"
      data-testid={testIds.maaterUiGallery.presenceCluster}
      style={{ zIndex: spread ? 5 : undefined }}
      onMouseEnter={() => setSpread(true)}
      onMouseLeave={() => {
        setSpread(false)
        setFrontId(null)
      }}
    >
      {members.map((m, i) => (
        <HoverCard
          key={m.id}
          member={m}
          style={{
            marginLeft: i === 0 ? 0 : spread ? gap : overlap,
            zIndex: frontId === m.id ? members.length + 2 : members.length - i,
            transition: `margin-left ${ease}`,
          }}
        >
          <button
            type="button"
            className="rounded-full focus:outline-none focus-visible:ring-1 focus-visible:ring-primary"
            onMouseEnter={() => setFrontId(m.id)}
            aria-label={`${m.screenName}, ${m.action}`}
          >
            <DemoAvatar member={m} size={size} />
          </button>
        </HoverCard>
      ))}
    </div>
  )
}

function MockTab({
  label,
  active,
  pip,
  onClick,
}: {
  label: string
  active: boolean
  pip?: DemoMember | null
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative flex max-w-[180px] items-center gap-1.5 truncate rounded-t-md px-3 py-1.5 text-xs"
      style={
        active
          ? {
              background: 'var(--paper)',
              color: 'var(--ink)',
              border: '1px solid var(--line)',
              borderBottomColor: 'var(--paper)',
              marginBottom: '-1px',
              fontWeight: 600,
            }
          : { color: 'var(--ink-soft)' }
      }
    >
      <span className="truncate">{label}</span>
      {pip ? (
        <HoverCard
          member={pip}
          linger
          className="group/pip"
          style={{ padding: 10, margin: -6 }}
        >
          <span
            className="inline-flex shrink-0 rounded-full transition-[transform,box-shadow] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover/pip:scale-125 group-hover/pip:shadow-[0_0_0_6px_hsl(var(--primary)/0.16)]"
          >
            <DemoAvatar member={pip} size={16} presence={active ? 'focused' : 'background'} />
          </span>
        </HoverCard>
      ) : null}
    </button>
  )
}

function memberFilterSummary(ids: string[]): string {
  if (ids.length === 0) return 'Showing edits from everyone. Click a person to narrow.'
  const names = MEMBERS.filter((m) => ids.includes(m.id)).map((m) => m.screenName)
  if (names.length === 1) return `Showing edits by ${names[0]}. Click that row again to remove them.`
  if (names.length === 2) return `Showing edits by ${names[0]} and ${names[1]}.`
  return `Showing edits by ${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}.`
}

export function PresenceScene() {
  const [placement, setPlacement] = useState<PlacementId>('e')
  const [focus, setFocus] = useState<FocusMode>('about-focused')
  const [memberIds, setMemberIds] = useState<string[]>([])
  const aboutActive = focus === 'about-focused'
  const showTitleCluster = placement === 'a' || (placement === 'e' && aboutActive)
  const showAboutPip = placement === 'b' || placement === 'e'
  const showNotesPip = placement === 'b' || placement === 'e'
  const others = MEMBERS
  const selected = PLACEMENTS.find((p) => p.id === placement)
  const filteredMembers = memberIds.length === 0
    ? MEMBERS
    : MEMBERS.filter((m) => memberIds.includes(m.id))
  const lastEditor = filteredMembers[0] ?? MEMBERS[0]

  function toggleMember(id: string) {
    setMemberIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  return (
      <div className="space-y-5" data-testid={testIds.maaterUiGallery.presenceScene}>
        <div className="space-y-2 text-sm" style={{ color: 'var(--ink-soft)' }}>
          <p>
            Placement E is locked: title-row cluster on the focused document, ghosted pip
            on background tabs. The presence chip and its tooltip stay a high-level abstract
            (who, and what they are doing here). Hover a bubble for the sketch of that card.
          </p>
          <p className="text-xs">
            Bet:{' '}
            <code className="text-xs">2026-08-28-member-identity-and-whos-here-presence</code>
            . The chip and tooltip are still an abstract, so there is no Primitive table
            import yet. <code>UserAvatar</code> / <code>formatScreenName</code> land with the
            chip component.
          </p>
        </div>

        <div>
          <p className="mb-2 text-[11px] font-medium uppercase tracking-wide" style={{ color: 'var(--ink-faint)' }}>
            Who's here placement
          </p>
          <div className="flex flex-wrap gap-1.5">
            {PLACEMENTS.map((p) => {
              const on = p.id === placement
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPlacement(p.id)}
                  className="rounded-md border px-2.5 py-1 text-xs"
                  style={{
                    borderColor: on ? 'var(--rust)' : 'var(--line)',
                    background: on ? 'var(--paper-raised)' : 'transparent',
                    color: 'var(--ink)',
                  }}
                  data-testid={testIds.maaterUiGallery.presencePlacement(p.id)}
                >
                  {p.label} {p.title}
                  {p.rank === 'locked' ? ' (locked)' : p.rank === 'out' ? ' (out)' : ''}
                </button>
              )
            })}
          </div>
          {selected && (
            <p className="mt-2 text-xs" style={{ color: 'var(--ink-soft)' }}>
              {selected.note}
            </p>
          )}
        </div>

        <div
          className="overflow-hidden rounded-md border"
          style={{ borderColor: 'var(--line)', background: 'var(--paper-raised)' }}
          data-testid={testIds.maaterUiGallery.presenceDocMock}
        >
          <div
            className={cn('flex items-end gap-0.5 border-b px-2', SHELL_COLUMN_HEADER_CLASS)}
            style={{ borderColor: 'var(--line)', background: 'var(--paper-deep)' }}
          >
            <MockTab
              label="about"
              active={aboutActive}
              pip={showAboutPip ? MEMBERS[0] : null}
              onClick={() => setFocus('about-focused')}
            />
            <MockTab
              label="notes"
              active={!aboutActive}
              pip={showNotesPip ? MEMBERS[1] : null}
              onClick={() => setFocus('notes-focused')}
            />
          </div>

          <div
            className="flex h-10 items-center gap-2 border-b px-3"
            style={{ borderColor: 'var(--line)' }}
          >
            <span className="min-w-0 flex-1 truncate text-sm font-medium" style={{ color: 'var(--ink)' }}>
              {aboutActive ? 'about' : 'notes'}
            </span>
            {showTitleCluster && aboutActive ? <AvatarCluster members={others} /> : null}
            {placement === 'd' && aboutActive ? (
              <span className="text-[11px]" style={{ color: 'var(--ink-faint)' }}>
                (see dock)
              </span>
            ) : null}
            <button
              type="button"
              className="flex h-7 w-7 items-center justify-center rounded-md"
              style={{ color: 'var(--ink-soft)' }}
              title="More"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </div>

          <div className="flex min-h-[168px]">
            <div className="min-w-0 flex-1 p-4">
              <p className="text-[11px] uppercase tracking-wide" style={{ color: 'var(--ink-faint)' }}>
                Page title
              </p>
              <div
                className="mt-1 rounded-md border px-3 py-2 text-sm"
                style={{ borderColor: 'var(--line)', color: 'var(--ink)' }}
              >
                {aboutActive ? 'about' : 'notes'}
              </div>
              <p className="mt-3 text-xs" style={{ color: 'var(--ink-faint)' }}>
                No per-field rings. Presence lives in chrome, not on the input.
              </p>
            </div>
            {placement === 'd' && (
              <aside
                className="w-[200px] shrink-0 border-l p-3"
                style={{ borderColor: 'var(--line)', background: 'var(--paper-deep)' }}
              >
                <p className="text-[11px] font-medium uppercase tracking-wide" style={{ color: 'var(--ink-faint)' }}>
                  Who's here
                </p>
                <div className="mt-2 space-y-2">
                  {others.map((m) => (
                    <HoverCard key={m.id} member={m} block>
                      <button type="button" className="flex w-full items-center gap-2 text-left">
                        <DemoAvatar member={m} size={22} />
                        <span className="text-xs" style={{ color: 'var(--ink)' }}>
                          {m.screenName}
                        </span>
                      </button>
                    </HoverCard>
                  ))}
                </div>
              </aside>
            )}
          </div>

          <div
            className="flex h-8 items-center justify-between border-t px-3 text-xs"
            style={{ borderColor: 'var(--line)', background: 'var(--paper-deep)', color: 'var(--ink-soft)' }}
          >
            <span>
              {placement === 'c' && aboutActive
                ? 'Chris, Tammy, Gilbert here · Saved just now'
                : 'Saved just now'}
            </span>
            <span style={{ color: 'var(--ink-faint)' }}>Markdown</span>
          </div>
        </div>

        <p className="text-xs" style={{ color: 'var(--ink-faint)' }}>
          Click the notes tab to see background vs focused. Rust ring = focused. Ghosted = tab open
          but not active.
        </p>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <p className="mb-2 text-[11px] font-medium uppercase tracking-wide" style={{ color: 'var(--ink-faint)' }}>
              Filter by member
            </p>
            <div
              className="overflow-hidden rounded-md border"
              style={{ borderColor: 'var(--line)', background: 'var(--paper-raised)' }}
              data-testid={testIds.maaterUiGallery.presenceMemberFilter}
            >
              <div className="border-b px-3 py-2 text-[11px] uppercase tracking-wide" style={{ borderColor: 'var(--line)', color: 'var(--ink-faint)' }}>
                Filter by member
              </div>
              <div className="space-y-0.5 p-1">
                <button
                  type="button"
                  onClick={() => setMemberIds([])}
                  aria-pressed={memberIds.length === 0}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
                    memberIds.length === 0
                      ? 'bg-primary/10 hover:bg-primary/15'
                      : 'hover:bg-muted/60',
                  )}
                >
                  <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                    {memberIds.length === 0 ? <Check className="h-3.5 w-3.5 text-primary" /> : null}
                  </span>
                  <span style={{ color: 'var(--ink)' }}>All members</span>
                </button>
                {MEMBERS.map((m) => {
                  const on = memberIds.includes(m.id)
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => toggleMember(m.id)}
                      aria-pressed={on}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
                        on ? 'bg-primary/10 hover:bg-primary/15' : 'hover:bg-muted/60',
                      )}
                    >
                      <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                        {on ? <Check className="h-3.5 w-3.5 text-primary" /> : null}
                      </span>
                      <DemoAvatar member={m} size={22} presence="focused" />
                      <span style={{ color: 'var(--ink)' }}>{m.screenName}</span>
                      <span className="truncate text-xs" style={{ color: 'var(--ink-faint)' }}>
                        {m.fullName}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
            <p className="mt-2 text-xs" style={{ color: 'var(--ink-soft)' }}>
              {memberFilterSummary(memberIds)}
            </p>
          </div>

          <div>
            <p className="mb-2 text-[11px] font-medium uppercase tracking-wide" style={{ color: 'var(--ink-faint)' }}>
              Last edit + history
            </p>
            <div
              className="space-y-2 rounded-md border p-3"
              style={{ borderColor: 'var(--line)', background: 'var(--paper-raised)' }}
            >
              <p className="flex items-center gap-2 text-xs" style={{ color: 'var(--ink-soft)' }}>
                Last edit by
                <DemoAvatar member={lastEditor} size={18} presence="focused" />
                <span style={{ color: 'var(--ink)' }}>{lastEditor.screenName}</span>
                <span>· {lastEditor.lastEdit}</span>
              </p>
              <div className="space-y-1 pt-1">
                {filteredMembers.map((m) => (
                  <HoverCard key={m.id} member={m} block>
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors hover:bg-muted"
                      style={{ color: 'var(--ink)' }}
                    >
                      <span className="relative">
                        <DemoAvatar member={m} size={24} presence="focused" />
                        <span
                          className="absolute -bottom-0.5 -right-0.5 flex h-3 w-3 items-center justify-center rounded-full"
                          style={{ background: 'var(--paper-raised)', color: 'var(--ink-soft)' }}
                        >
                          <Pencil className="h-2 w-2" />
                        </span>
                      </span>
                      <span className="flex-1">
                        Edited · {m.screenName}
                      </span>
                      <span style={{ color: 'var(--ink-faint)' }}>{m.lastEdit}</span>
                    </button>
                  </HoverCard>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
  )
}
